import React, { useEffect, useRef, useState } from 'react';
import { withTranslation, useTranslation } from 'react-i18next';
import './Stations.css';

/* ── Icono SVG por tipo de estación ── */
const TYPE_ICONS = {
  meteo: (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
      stroke="#185FA5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4"/>
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2"/>
    </svg>
  ),
  soil: (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
      stroke="#3B6D11" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 22h20M6 18v4M10 14v8M14 10v12"/>
    </svg>
  ),
  air: (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
      stroke="#534AB7" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.7 7.7a2.5 2.5 0 111.8 4.3H2M9.6 4.6a2 2 0 113 2.4H2"/>
    </svg>
  ),
};

/* Color del dot por tipo */
const SENSOR_COLORS = {
  meteo: '#378ADD',
  soil:  '#639922',
  air:   '#7F77DD',
};

/* ── Card individual ── */
const StationCard = ({ station, t, data }) => {
  const { type, imgKey, imgAltKey, modelKey, nameKey, descKey, sensorsKey, linkKey } = station;

  const imgSrc   = t(`${data}.${imgKey}`);
  const imgAlt   = t(`${data}.${imgAltKey}`);
  const model    = t(`${data}.${modelKey}`);
  const name     = t(`${data}.${nameKey}`);
  const desc     = t(`${data}.${descKey}`);
  const linkText = t(`${data}.${linkKey}`);
  const sensors  = t(`${data}.${sensorsKey}`, { returnObjects: true });

  const sensorColor = SENSOR_COLORS[type];

  const scrollToContact = () => {
    const el = document.getElementById('contact');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.hash = '#contact';
    }
  };

  return (
    <div className="xAgro-stations-card">

      {/* Imagen con badge de tipo */}
      <div className="xAgro-stations-img-wrap">
        <img
          src={imgSrc}
          alt={imgAlt}
          className="xAgro-stations-img"
          loading="lazy"
        />
        <span className={`xAgro-stations-type-badge xAgro-stations-type-badge--${type}`}>
          {TYPE_ICONS[type]}
          {t(`${data}.type_${type}`)}
        </span>
      </div>

      {/* Cuerpo */}
      <div className="xAgro-stations-body">
        <p className="xAgro-stations-model">{model}</p>
        <h3 className="xAgro-stations-name">{name}</h3>
        <p className="xAgro-stations-desc">{desc}</p>

        <div className="xAgro-stations-divider" />

        {/* Chips de sensores */}
        {Array.isArray(sensors) && sensors.length > 0 && (
          <div className="xAgro-stations-sensors">
            {sensors.map((sensor, i) => (
              <span key={i} className="xAgro-stations-sensor-chip">
                <span
                  className="xAgro-stations-sensor-dot"
                  style={{ background: sensorColor }}
                />
                {sensor}
              </span>
            ))}
          </div>
        )}

        {/* Link a contacto */}
        <div className="xAgro-stations-card-footer">
          <button
            className="xAgro-stations-card-link"
            onClick={scrollToContact}
            aria-label={linkText}
          >
            {linkText}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5"
              strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Configuración de las tres estaciones ──
   Solo contiene la lógica de la vista: qué clave del JSON
   corresponde a cada campo. Los textos viven en common.json. */
const STATIONS = [
  {
    type:      'meteo',
    imgKey:    'station_meteo_img',
    imgAltKey: 'station_meteo_img_alt',
    modelKey:  'station_meteo_model',
    nameKey:   'station_meteo_name',
    descKey:   'station_meteo_desc',
    sensorsKey:'station_meteo_sensors',
    linkKey:   'station_link',
  },
  {
    type:      'soil',
    imgKey:    'station_soil_img',
    imgAltKey: 'station_soil_img_alt',
    modelKey:  'station_soil_model',
    nameKey:   'station_soil_name',
    descKey:   'station_soil_desc',
    sensorsKey:'station_soil_sensors',
    linkKey:   'station_link',
  },
  {
    type:      'air',
    imgKey:    'station_air_img',
    imgAltKey: 'station_air_img_alt',
    modelKey:  'station_air_model',
    nameKey:   'station_air_name',
    descKey:   'station_air_desc',
    sensorsKey:'station_air_sensors',
    linkKey:   'station_link',
  },
];

/* ── Componente principal ── */
const Stations = function Stations({ data }) {
  const [t] = useTranslation('common');
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  // ── Textos desde common.json → xAgro.stations ──
  const eyebrow  = t(`${data}.eyebrow`);
  const title    = t(`${data}.title`);
  const subtitle = t(`${data}.subtitle`);
  const ctaText  = t(`${data}.cta`);

  // Specs del footer compartidos por los tres modelos
  const specConnLabel = t(`${data}.spec_conn_label`);
  const specConnValue = t(`${data}.spec_conn_value`);
  const specPowerLabel = t(`${data}.spec_power_label`);
  const specPowerValue = t(`${data}.spec_power_value`);
  const specIpLabel   = t(`${data}.spec_ip_label`);
  const specIpValue   = t(`${data}.spec_ip_value`);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.08 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const scrollToContact = () => {
    const el = document.getElementById('contact');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.hash = '#contact';
    }
  };

  return (
    <section
      id="xAgroStations"
      className="xAgro-stations-section"
      ref={sectionRef}
    >
      <div className="xAgro-stations-wrapper">

        {/* ── Encabezado ── */}
        <div className="xAgro-stations-header">
          <div className="xAgro-stations-eyebrow">
            {/* Reutiliza el badge global de tokens */}
            <span className="xagro-badge xagro-badge--light">
              <span className="xagro-badge-dot xagro-badge-dot--animated" />
              {eyebrow}
            </span>
          </div>
          <h2 className="xAgro-stations-title">{title}</h2>
          <p className="xAgro-stations-subtitle">{subtitle}</p>
        </div>

        {/* ── Grid de cards ── */}
        <div className="xAgro-stations-grid">
          {STATIONS.map((station) => (
            <StationCard
              key={station.type}
              station={station}
              t={t}
              data={data}
            />
          ))}
        </div>

        {/* ── Footer: specs compartidas + CTA ── */}
        <div className="xAgro-stations-footer">
          <div className="xAgro-stations-specs">
            <div>
              <span className="xAgro-stations-spec-value">{specConnValue}</span>
              <span className="xAgro-stations-spec-label">{specConnLabel}</span>
            </div>
            <div>
              <span className="xAgro-stations-spec-value">{specPowerValue}</span>
              <span className="xAgro-stations-spec-label">{specPowerLabel}</span>
            </div>
            <div>
              <span className="xAgro-stations-spec-value">{specIpValue}</span>
              <span className="xAgro-stations-spec-label">{specIpLabel}</span>
            </div>
          </div>

          <button className="xAgro-stations-cta" onClick={scrollToContact}>
            {ctaText}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5"
              strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </button>
        </div>

      </div>
    </section>
  );
};

export default withTranslation('common')(Stations);